import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sectorService, socketService } from '../services/api';
import Sidebar from './Sidebar';
import DistanceToMA from './DistanceToMA';
import SmartBoard from './SmartBoard';
import UserManagement from './UserManagement';
import BuySignals from './BuySignals';
import './Dashboard.css';

const Dashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [sectors, setSectors] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Shared socket data — lives at Dashboard level, never unmounts
  const [stockData, setStockData] = useState({});
  const [marketIndices, setMarketIndices] = useState([]);
  const [min22Data, setMin22Data] = useState({});
  const [earlySignalsData, setEarlySignalsData] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketError, setSocketError] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Fetch sectors once at Dashboard level, shared by all child components
  const fetchSectors = useCallback(async () => {
    try {
      const response = await sectorService.getSectors();
      setSectors(response.sectors || []);
    } catch (err) {
      console.error('Failed to fetch sectors:', err);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  // Socket connection — ONE connection for the entire app lifetime
  useEffect(() => {
    const socket = socketService.connect();

    const onConnect = () => {
      setSocketConnected(true);
      setSocketError(null);
    };

    const onDisconnect = () => {
      setSocketConnected(false);
    };

    const onConnectError = (err) => {
      console.error('Connection error:', err);
      setSocketError('Failed to connect to server');
    };

    const onSmartBoardUpdate = (data) => {
      if (data && data.all_stocks) {
        const dataMap = {};
        data.all_stocks.forEach(stock => {
          dataMap[stock.symbol] = stock;
        });
        setStockData(dataMap);
        setInitialLoadDone(true);
      }
    };

    const onMarketIndicesUpdate = (data) => {
      if (data && data.indices) {
        setMarketIndices(data.indices);
      }
    };

    const onMin22Update = (data) => {
      if (data && data.data) {
        setMin22Data(data.data);
      }
    };

    const onEarlySignalsUpdate = (data) => {
      if (data && data.data) {
        setEarlySignalsData(data);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('smart_board_update', onSmartBoardUpdate);
    socket.on('market_indices_update', onMarketIndicesUpdate);
    socket.on('min22_update', onMin22Update);
    socket.on('early_signals_update', onEarlySignalsUpdate);

    if (socket.connected) {
      setSocketConnected(true);
    }

    // Cleanup only on Dashboard unmount (logout)
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('smart_board_update', onSmartBoardUpdate);
      socket.off('market_indices_update', onMarketIndicesUpdate);
      socket.off('min22_update', onMin22Update);
      socket.off('early_signals_update', onEarlySignalsUpdate);
      socketService.disconnect();
    };
  }, []);

  // Redirect unknown paths to /smart-board
  useEffect(() => {
    const validPaths = ['/smart-board', '/distance-to-ma', '/buy-signals', '/user-management'];
    if (!validPaths.includes(location.pathname)) {
      navigate('/smart-board', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderContent = () => {
    switch (location.pathname) {
      case '/smart-board':
        return (
          <SmartBoard
            sectors={sectors}
            setSectors={setSectors}
            fetchSectors={fetchSectors}
            stockData={stockData}
            marketIndices={marketIndices}
            min22Data={min22Data}
            isConnected={socketConnected}
            socketError={socketError}
            initialLoadDone={initialLoadDone}
            earlySignalsData={earlySignalsData}
          />
        );
      case '/distance-to-ma':
        return <DistanceToMA sectors={sectors} />;
      case '/buy-signals':
        return (
          <BuySignals
            sectors={sectors}
            stockData={stockData}
            min22Data={min22Data}
          />
        );
      case '/user-management':
        return <UserManagement />;
      default:
        return (
          <SmartBoard
            sectors={sectors}
            setSectors={setSectors}
            fetchSectors={fetchSectors}
            stockData={stockData}
            marketIndices={marketIndices}
            min22Data={min22Data}
            isConnected={socketConnected}
            socketError={socketError}
            initialLoadDone={initialLoadDone}
            earlySignalsData={earlySignalsData}
          />
        );
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar
        user={user}
        onLogout={handleLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {renderContent()}
      </main>
    </div>
  );
};

export default Dashboard;
